import { useState } from "react";
import {
  useDownloadEvidenceFile,
  useEvidenceFiles,
} from "@/hooks/useEvidenceFiles";
import type {
  EvidenceDownloadReceipt,
  EvidenceFileEntry,
  EvidenceFileKind,
} from "@/types";
import type { EvidenceFailure } from "@/utils/evidenceError";
import {
  evidenceIntegrityColor,
  evidenceIntegrityLabels,
  evidenceIntegrityNote,
  evidenceKindLabels,
  evidenceStateColor,
  evidenceStateLabels,
  evidenceStateNote,
  formatEvidenceSize,
  storedHashScopeLabels,
  transferColor,
  transferLabel,
  transferNote,
} from "@/utils/evidenceLabels";

/**
 * 시료의 **증거 원본 파일** — 상태를 정직하게 보여 주고, 있는 것만 내려준다.
 *
 * 2026-09-07 조사 전까지 이 플랫폼에는 원본을 꺼내는 경로가 어떤 역할로도
 * 없었다. 판정의 근거가 된 스펙트럼 원본은 SSH 로만 꺼낼 수 있었다.
 *
 * 이 카드의 핵심은 "내려받기 버튼"이 아니라 **여러 종류의 "없음"을 구분해
 * 말하는 것**이다 (2026-09-07 prod 실측: 시료 207개 중 원본을 되찾을 수
 * 있는 것은 9개, 소실 3개, 나머지 195개는 저장된 적이 없다):
 *
 * - 저장된 적 없음 → 결함이 아니라 사실이다. 경고처럼 보이면 안 된다.
 * - 소실 → 있었던 것이 없어졌다. 조용히 넘어가면 안 된다.
 * - 확인 불가 → 모른다. 없다고도, 없어졌다고도 하지 않는다.
 * - 보관 중 → 내려받을 수 있다.
 */

/** 64자 해시는 감정서에 옮겨 적어야 하는 값이다 — 전문을 얻을 수 있어야 한다. */
function Hash({
  value,
  label,
}: {
  value: string | null | undefined;
  label: string;
}) {
  const [copied, setCopied] = useState(false);
  if (!value) {
    return <span className="text-gray-500">기록 없음</span>;
  }
  return (
    <span className="inline-flex items-baseline gap-1 min-w-0">
      <code className="font-mono text-xs text-gray-800 break-all">{value}</code>
      <button
        type="button"
        aria-label={`${label} 전체 복사`}
        className="shrink-0 text-xs text-blue-600 hover:text-blue-800 underline"
        onClick={async () => {
          try {
            await navigator.clipboard?.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch {
            setCopied(false);
          }
        }}
      >
        {copied ? "복사됨" : "복사"}
      </button>
    </span>
  );
}

function Badge({ text, color }: { text: string; color: string }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}
    >
      {text}
    </span>
  );
}

function Receipt({ receipt }: { receipt: EvidenceDownloadReceipt }) {
  const integrity = receipt.integrity;
  return (
    <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
      <div className="flex items-center gap-2 flex-wrap min-w-0">
        <span className="text-sm font-medium text-gray-900 break-all">
          {receipt.fileName} 내려받음 ({formatEvidenceSize(receipt.sizeBytes)})
        </span>
        <Badge
          text={transferLabel(receipt.transferOk)}
          color={transferColor(receipt.transferOk)}
        />
        {integrity ? (
          <Badge
            // 우리가 모르는 값이면 원문이라도 보여 준다 — 빈 뱃지는 침묵이다.
            text={evidenceIntegrityLabels[integrity] ?? integrity}
            color={evidenceIntegrityColor(integrity)}
          />
        ) : (
          <Badge text="무결성 표시 없음" color="text-gray-700 bg-gray-100" />
        )}
      </div>

      <p className="mt-1 text-xs text-gray-600">
        {transferNote(receipt.transferOk)}
      </p>
      <p className="mt-1 text-xs text-gray-600">
        {integrity
          ? (evidenceIntegrityNote[integrity] ??
            "서버가 보낸 무결성 표시를 해석하지 못했습니다.")
          : "서버가 무결성 확인 결과를 보내지 않았습니다 — 확인되지 않았다는 뜻이며, 이상이 없다는 뜻이 아닙니다."}
      </p>

      <dl className="mt-2 space-y-1 text-xs">
        <div className="flex flex-wrap gap-x-2">
          <dt className="text-gray-500 shrink-0">
            받은 파일 해시(브라우저 계산)
          </dt>
          <dd className="min-w-0">
            <Hash value={receipt.receivedSha256} label="받은 파일 해시" />
          </dd>
        </div>
        <div className="flex flex-wrap gap-x-2">
          <dt className="text-gray-500 shrink-0">서버 재계산 해시</dt>
          <dd className="min-w-0">
            <Hash value={receipt.serverSha256} label="서버 재계산 해시" />
          </dd>
        </div>
        <div className="flex flex-wrap gap-x-2">
          <dt className="text-gray-500 shrink-0">등록 당시 해시</dt>
          <dd className="min-w-0">
            <Hash value={receipt.storedSha256} label="등록 당시 해시" />
          </dd>
        </div>
      </dl>
    </div>
  );
}

function Failure({ failure }: { failure: EvidenceFailure }) {
  // "저장된 적 없음" 은 사건이 아니라 사실이다 — 경고색을 쓰지 않는다.
  const neutral = failure.code === "EVIDENCE_NEVER_STORED";
  return (
    <div
      className={`mt-2 rounded-lg p-3 border ${
        neutral ? "bg-gray-50 border-gray-200" : "bg-red-50 border-red-200"
      }`}
    >
      <p className={`text-sm ${neutral ? "text-gray-800" : "text-red-800"}`}>
        {failure.message}
      </p>
      {failure.code === "EVIDENCE_LOST" && failure.storedHash && (
        <p className="mt-1 text-xs text-gray-600">
          등록 당시 해시는 기록에 남아 있습니다 — 원본을 되찾으면 대조할 수
          있습니다. <Hash value={failure.storedHash} label="등록 당시 해시" />
        </p>
      )}
    </div>
  );
}

interface RowProps {
  entry: EvidenceFileEntry;
  isPending: boolean;
  receipt?: EvidenceDownloadReceipt;
  failure?: EvidenceFailure;
  onDownload: (kind: EvidenceFileKind, fileName: string | null) => void;
}

function FileRow({ entry, isPending, receipt, failure, onDownload }: RowProps) {
  // 서버가 "있다"고 하면서 내려받기를 막는 경우 — 있다는 사실과 지금 받을 수
  // 없다는 사실을 함께 말한다.
  const availableButBlocked =
    entry.state === "available" && !entry.downloadable;

  return (
    <li className="py-3 border-t border-gray-100 first:border-t-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">
              {evidenceKindLabels[entry.kind] ?? entry.kind}
            </span>
            <Badge
              text={evidenceStateLabels[entry.state] ?? entry.state}
              color={evidenceStateColor(entry.state)}
            />
          </div>
          <p className="mt-1 text-sm text-gray-700 break-all">
            {entry.file_name || (
              <span className="text-gray-500">이름 기록 없음</span>
            )}
            {entry.size_bytes != null && (
              <span className="text-gray-500">
                {" "}
                ({formatEvidenceSize(entry.size_bytes)})
              </span>
            )}
          </p>
          {/* 상태의 뜻을 눈에 보이는 문장으로 적는다. 비활성 버튼의 title 에
              숨기면 스크린리더도 키보드도 닿지 못한다. */}
          <p className="mt-1 text-xs text-gray-500">
            {availableButBlocked
              ? "원본은 저장소에 있으나 지금은 내려받을 수 없습니다."
              : (evidenceStateNote[entry.state] ??
                "이 상태를 해석하지 못했습니다.")}
          </p>
        </div>

        {entry.downloadable && (
          <button
            type="button"
            // disabled 대신 aria-busy + 클릭 가드를 쓴다. disabled 로 만들면
            // 방금 누른 버튼이 포커스를 잃어 <body> 로 떨어진다 — 이 프로젝트가
            // 지난주에 이미 겪은 결함이다.
            aria-busy={isPending}
            onClick={() => {
              if (isPending) return;
              onDownload(entry.kind, entry.file_name);
            }}
            className={`shrink-0 px-3 py-1.5 text-sm font-medium rounded-lg text-white ${
              isPending
                ? "bg-gray-400 cursor-progress"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isPending ? "내려받는 중…" : "원본 내려받기"}
          </button>
        )}
      </div>

      {/* 결과는 해당 행 밑에 붙인다 — 어느 파일 얘기인지 분명해야 한다. */}
      <output aria-live="polite" className="block">
        {isPending && (
          <p className="mt-2 text-xs text-gray-600">
            {entry.file_name || evidenceKindLabels[entry.kind]} 내려받는 중…
          </p>
        )}
        {receipt && <Receipt receipt={receipt} />}
        {failure && <Failure failure={failure} />}
      </output>
    </li>
  );
}

export default function SampleOriginalFilesCard({
  sampleId,
}: {
  sampleId: number;
}) {
  const { data, isLoading, error } = useEvidenceFiles(sampleId);
  const { pending, receipts, failures, download } =
    useDownloadEvidenceFile(sampleId);

  const shell = "bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6";
  const heading = (
    <h2 className="text-lg font-semibold text-gray-900 mb-1">증거 원본</h2>
  );

  if (isLoading) {
    return (
      <div className={shell}>
        {heading}
        {/* <output> 은 role="status" 를 암시한다 — 스켈레톤이 보조기술에
            보이지 않던 것을 고치면서 프로젝트 관례(<output aria-live>)도 따른다. */}
        <output aria-busy="true" className="block animate-pulse mt-3">
          <span className="sr-only">증거 원본 상태를 불러오는 중</span>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
        </output>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={shell}>
        {heading}
        <div
          role="alert"
          className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3"
        >
          <p className="text-sm text-red-700">
            원본 상태를 불러오지 못했습니다. 이는 원본이 없다는 뜻이 아니라,
            상태를 확인하지 못했다는 뜻입니다.
          </p>
        </div>
      </div>
    );
  }

  // 서버가 목록 자체를 비워 보내면 우리는 **아무것도 모르는** 상태다.
  // 그걸 "저장된 적 없음" 으로 바꿔 말하면 확인하지 않은 사실을 단정하게 된다.
  if (data.files.length === 0) {
    return (
      <div className={shell}>
        {heading}
        <div
          role="alert"
          className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3"
        >
          <p className="text-sm text-amber-800">
            원본 상태를 알 수 없습니다. 서버가 파일 정보를 돌려주지 않았습니다 —
            원본이 없다는 뜻은 아닙니다.
          </p>
        </div>
      </div>
    );
  }

  const recorded = data.files.filter((f) => f.state !== "never_stored");
  const neverStored = data.files.filter((f) => f.state === "never_stored");
  const anyLost = recorded.some((f) => f.state === "lost");

  return (
    <div className={shell}>
      {heading}
      <p className="text-xs text-gray-500 mb-4">
        분석의 근거가 된 스펙트럼 원본 파일입니다. 내려받기는 감사 기록에
        남습니다.
      </p>

      {recorded.length === 0 ? (
        // 195개 시료의 상황. 결함이 아니라 사실이므로 경고색을 쓰지 않는다.
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-800 font-medium">
            이 시료는 원본 파일이 저장된 적이 없습니다.
          </p>
          <p className="mt-1 text-sm text-gray-600">
            파싱된 스펙트럼 결과만 보관되어 있으며, 제출할 원본 파일은 존재하지
            않습니다. 파일이 없어진 것이 아니라 처음부터 저장되지 않았습니다.
          </p>
        </div>
      ) : (
        <>
          {anyLost && (
            <div
              className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3"
              role="alert"
            >
              <p className="text-sm text-red-800">
                이 시료의 원본 일부가 <strong>소실</strong>되었습니다. 등록
                기록은 남아 있으나 저장소에서 파일을 찾을 수 없습니다.
              </p>
            </div>
          )}
          <ul className="mb-1">
            {recorded.map((entry) => (
              <FileRow
                key={entry.kind}
                entry={entry}
                isPending={Boolean(pending[entry.kind])}
                receipt={receipts[entry.kind]}
                failure={failures[entry.kind]}
                onDownload={download}
              />
            ))}
          </ul>
          {neverStored.length > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              {neverStored
                .map((f) => evidenceKindLabels[f.kind] ?? f.kind)
                .join(" · ")}{" "}
              — 저장된 적 없음
            </p>
          )}
        </>
      )}

      {data.stored_hash && (
        <div className="mt-4 pt-3 border-t border-gray-100 space-y-1 text-xs">
          <div className="flex flex-wrap gap-x-2">
            <span className="text-gray-500 shrink-0">등록 당시 해시</span>
            <Hash value={data.stored_hash} label="등록 당시 해시" />
          </div>
          <div className="text-gray-500">
            해시 범위{" "}
            <span className="text-gray-800">
              {storedHashScopeLabels[data.stored_hash_scope] ??
                data.stored_hash_scope}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
